"use client";

// SidebarWidgetInspector — the Tab 3 surface inside the expanded Sidebar.
// Three stacked sections, top-down:
//   1. Widget list   — all active widgets, click to select.
//   2. Settings      — selected widget's config (B1 schema if defined, else
//                      a placeholder block with description + a hint about how
//                      to declare settings).
//   3. Preview       — live render of the widget's `renderBar` or `component`.
//
// Purpose (per user): "ההגדרות של כל ווידג'ט בנפרד מהנראות שלו במקום אחד
// קבוע, מבלי לעבור לעמוד של כל האפליקציות, ולסדר תוך כדי עבודה".
//
// Selection persists to localStorage so the inspector remembers what was last
// open across reloads.
//
// Future: each widget can opt in to a settings schema (same shape as the new
// BlockFieldSchema). Until then, this surface is the canonical place to ADD
// widget settings — write once here, render everywhere.

import { useEffect, useState, useMemo, useCallback, Suspense } from "react";
import { ChevronRight, PanelRight, LayoutDashboard, ArrowUpToLine, EyeOff } from "lucide-react";
import { widgetRegistry, getWidgetById, getEffectivePlacement, type WidgetPlacement } from "./widgets/WidgetRegistry";
import { useSettings } from "@/contexts/SettingsContext";
import { useWidgets } from "@/contexts/WidgetContext";
import { WidgetSettingsEditor } from "./WidgetSettingsEditor";

const STORAGE_KEY = "cc-widget-inspector-selected";

export function SidebarWidgetInspector() {
  const { language } = useSettings();
  const { widgetPlacements, setWidgetPlacement } = useWidgets();
  const isRtl = language === "he";
  const langKey: "he" | "en" | "ru" = language === "en" ? "en" : language === "ru" ? "ru" : "he";

  // Default selection: first active widget.
  const activeWidgets = useMemo(
    () => widgetRegistry.filter((w) => w.status === "active"),
    []
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const openInRailPanel = useCallback((id: string) => {
    window.dispatchEvent(new CustomEvent("cc-widget-rail-open", { detail: id }));
  }, []);

  const changePlacement = useCallback((id: string, placement: WidgetPlacement) => {
    setWidgetPlacement(id, placement);
  }, [setWidgetPlacement]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && getWidgetById(saved)?.status === "active") {
        setSelectedId(saved);
        return;
      }
    } catch { /* ignore */ }
    // No saved selection — default to the first active widget if any.
    if (activeWidgets[0]) setSelectedId(activeWidgets[0].id);
  }, [activeWidgets]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  };

  const selected = selectedId ? getWidgetById(selectedId) : null;

  return (
    <div
      data-cc-id="sidebar.widget-inspector"
      data-info-id="sidebar-widget-inspector"
      className="flex-1 overflow-hidden flex flex-col"
    >
      {/* ── Section 1: Widget list (top, scrollable, max ~30% height) ── */}
      <section className="shrink-0 border-b border-white/[0.06]">
        <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
          {isRtl ? "ווידג׳טים זמינים" : "Widgets"}
          <span className="ms-1.5 text-neutral-600 tabular-nums">{activeWidgets.length}</span>
        </div>
        <div className="max-h-[180px] overflow-y-auto px-1.5 pb-1.5">
          {activeWidgets.map((w) => {
            const Icon = w.icon;
            const active = w.id === selectedId;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => handleSelect(w.id)}
                title={w.description[langKey]}
                className={`group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start transition-colors ${
                  active
                    ? "app-accent-soft-15 app-accent-text"
                    : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate text-[12px] font-medium">
                  {w.label[langKey]}
                </span>
                <ChevronRight
                  className={`h-3 w-3 shrink-0 opacity-0 transition-opacity ${active ? "opacity-100" : "group-hover:opacity-60"} ${isRtl ? "rotate-180" : ""}`}
                />
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Section 2: Settings (middle, scrollable) ── */}
      <section className="flex-1 min-h-0 overflow-y-auto border-b border-white/[0.06]">
        {selected ? (
          <div className="p-3">
            <div className="mb-2 flex items-center gap-2">
              <selected.icon className="h-4 w-4 app-accent-text" />
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-white truncate">
                  {selected.label[langKey]}
                </div>
                <div className="text-[10px] text-neutral-500 truncate">
                  {isRtl ? "הגדרות הווידג׳ט" : "Widget settings"}
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-[11px] text-neutral-400 leading-relaxed mb-3">
              {selected.description[langKey]}
            </p>

            {/* Metadata badges */}
            <div className="mb-3 flex flex-wrap gap-1">
              <MetaBadge label={selected.category} />
              <MetaBadge label={selected.tier} tone={selected.tier === "free" ? "neutral" : selected.tier === "pro" ? "accent" : "warn"} />
              <MetaBadge label={`${selected.defaultSize}×`} />
              {selected.panelMode && <MetaBadge label={selected.panelMode} />}
              {selected.placement && <MetaBadge label={selected.placement} tone="accent" />}
            </div>

            {/* Actions */}
            <div className="space-y-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500 mb-1">
                {isRtl ? "פעולות" : "Actions"}
              </div>

              {/* Open in side panel — only when widget supports it */}
              {(selected.placement === "both" || selected.placement === "sidebar-rail") && (
                <ActionButton
                  icon={PanelRight}
                  label={isRtl ? "פתח בפאנל הצדדי" : "Open in side panel"}
                  onClick={() => openInRailPanel(selected.id)}
                />
              )}

              {/* Placement: TopBar / AppDock / Disabled */}
              {(() => {
                const current = getEffectivePlacement(selected.id, widgetPlacements, selected.isRemovable);
                return (
                  <>
                    <ActionButton
                      icon={ArrowUpToLine}
                      label={isRtl ? "הצג ב-TopBar" : "Show in TopBar"}
                      onClick={() => changePlacement(selected.id, "toolbar")}
                      active={current === "toolbar"}
                    />
                    <ActionButton
                      icon={LayoutDashboard}
                      label={isRtl ? "הצמד ל-Dock" : "Pin to Dock"}
                      onClick={() => changePlacement(selected.id, "apps")}
                      active={current === "apps"}
                    />
                    {selected.isRemovable && (
                      <ActionButton
                        icon={EyeOff}
                        label={isRtl ? "הסתר" : "Disable"}
                        onClick={() => changePlacement(selected.id, "disabled")}
                        active={current === "disabled"}
                      />
                    )}
                  </>
                );
              })()}
            </div>

            {/* Schema-driven settings — only renders when the widget opts in
                by declaring `settingsSchema` on its WidgetDefinition. */}
            {selected.settingsSchema && selected.settingsSchema.length > 0 && (
              <div className="mt-4 border-t border-white/[0.06] pt-3">
                <WidgetSettingsEditor
                  widgetId={selected.id}
                  schema={selected.settingsSchema}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-center text-[11px] text-neutral-500">
            {isRtl ? "בחר ווידג׳ט מהרשימה למעלה" : "Pick a widget from the list above"}
          </div>
        )}
      </section>

      {/* ── Section 3: Preview (bottom, fixed height) ── */}
      {selected && (
        <section className="shrink-0">
          <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
            {isRtl ? "תצוגה מקדימה" : "Preview"}
          </div>
          <div className="px-3 pb-3">
            <div
              className="rounded-lg border border-white/[0.06] bg-slate-800/30 backdrop-blur-md p-2 min-h-[60px] flex items-center justify-center"
              data-cc-id="sidebar.widget-inspector.preview"
            >
              <Suspense fallback={<span className="text-[10px] text-neutral-500">…</span>}>
                {renderWidgetPreview(selected)}
              </Suspense>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

interface MetaBadgeProps {
  label: string;
  tone?: "neutral" | "accent" | "warn";
}

function MetaBadge({ label, tone = "neutral" }: MetaBadgeProps) {
  const toneClass = tone === "accent"
    ? "bg-[var(--cc-accent-500)]/15 text-[var(--cc-accent-300)] border-[var(--cc-accent-500)]/20"
    : tone === "warn"
      ? "bg-amber-500/15 text-amber-300 border-amber-500/20"
      : "bg-white/[0.04] text-neutral-400 border-white/[0.06]";
  return (
    <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9px] uppercase tracking-wide ${toneClass}`}>
      {label}
    </span>
  );
}

interface ActionButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
}

function ActionButton({ icon: Icon, label, onClick, active }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-[11px] transition-colors ${
        active
          ? "app-accent-soft-15 app-accent-text"
          : "text-neutral-300 hover:bg-white/[0.04] hover:text-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80 group-hover:opacity-100" />
      <span className="flex-1 truncate">{label}</span>
      {active && <span className="text-[9px] text-[var(--cc-accent-400)]">●</span>}
    </button>
  );
}

function renderWidgetPreview(w: { renderBar?: React.ComponentType<{ size: 1 | 2 | 3 | 4 }>; component?: React.ComponentType; label: { he: string; en: string; ru: string } }) {
  // Prefer renderBar (smaller, inline-friendly). Fall back to component.
  if (w.renderBar) {
    const RenderBar = w.renderBar;
    return <RenderBar size={2} />;
  }
  if (w.component) {
    const Component = w.component;
    return (
      <div className="max-w-full overflow-hidden">
        <Component />
      </div>
    );
  }
  return (
    <span className="text-[10px] text-neutral-500">
      (no preview available)
    </span>
  );
}
